/**
 * @file index.tsx
 *
 * @fileoverview Shows a public organization profile.
 */

import { useQuery } from '@apollo/client';
import dynamic from 'next/dynamic';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import JobTeaserCard from '~/jobiqo/components/Job/Teaser';
import Link from '~/jobiqo/components/Link';
import { OrganizationWidgetsBoxLabel } from '~/jobiqo/components/Organization/Widgets/BoxLabel';
import { Job, OrganizationProfile } from '~/jobiqo/types/types';
import { MessageAlert } from '~/vcl/atoms/02-messages/01-alert';
import { Cell } from '~/vcl/atoms/06-layout/04-grid/01-cell';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { Box } from '~/vcl/atoms/07-box';
import { JobTeaserPlaceholder } from '~/vcl/organisms/05-placeholders/03-job/03-teaser';
import styles from './jobsByOrganization.module.scss';
import GET_JOBS_BY_ORGANIZATION_PROFILE_ID from './jobsByOrganizationProfileId.gql';

const BookmarkedJobsProvider = dynamic(
  import('~/jobiqo/components/BookmarkedJobs/context'),
);

// Limit for deciding whether show more link should be shown.
// Number of jobs shown is set in the backend.
const JOBS_LIMIT = 6;

interface JobsByOrganizationProfileIdProps {
  organization: OrganizationProfile;
  /**
   * The current job id in case we render organization jobs on job detail page.
   */
  currentJobId?: number | null;
}

export const JobsByOrganizationProfileId = ({
  organization,
  currentJobId = null,
}: JobsByOrganizationProfileIdProps) => {
  let jobs: Job[] = [];
  const intl = useIntl();
  const { loading, error, data } = useQuery(
    GET_JOBS_BY_ORGANIZATION_PROFILE_ID,
    {
      variables: {
        id: organization.id,
        currentJobId: currentJobId,
      },
    },
  );

  if (loading) {
    return (
      <Box>
        <div className={styles.responsiveGrid}>
          <Cell>
            <JobTeaserPlaceholder />
          </Cell>
          <Cell>
            <JobTeaserPlaceholder />
          </Cell>
          <Cell>
            <JobTeaserPlaceholder />
          </Cell>
          <Cell>
            <JobTeaserPlaceholder />
          </Cell>
        </div>
      </Box>
    );
  }

  if (error) {
    return (
      <div>
        <Spacing />
        <MessageAlert type="assertive" alertStyle="danger">
          <FormattedMessage
            id="organization_jobs_error"
            defaultMessage="Error getting jobs information."
          />
        </MessageAlert>
      </div>
    );
  }

  if (data && data.jobsByOrganizationProfileId) {
    jobs = data.jobsByOrganizationProfileId;
  }

  return jobs.length > 0 ? (
    <Box
      label={
        <OrganizationWidgetsBoxLabel>
          {intl.formatMessage({
            id: 'organization_job_list',
            defaultMessage: 'Our jobs',
          })}
        </OrganizationWidgetsBoxLabel>
      }
    >
      <div className={styles.responsiveGrid}>
        <BookmarkedJobsProvider
          initialState={jobs.filter((job) => job.bookmarked)}
        >
          {jobs.map((job: Job) => {
            return (
              <Cell display="grid" key={job.id}>
                <JobTeaserCard job={job} />
              </Cell>
            );
          })}
        </BookmarkedJobsProvider>
      </div>
      <Spacing paddingTop={1} />
      {jobs.length >= JOBS_LIMIT && (
        <Link
          href={{
            pathname: '/jobs',
            query: { job_organization_name: organization.name },
          }}
        >
          <FormattedMessage
            id="organization_show_more_jobs_label"
            defaultMessage="Show more"
          />
        </Link>
      )}
    </Box>
  ) : null;
};
export default JobsByOrganizationProfileId;
